import React from "react";
import { AlertTriangle, Building2, ArrowRight } from "lucide-react";
import { Button } from "./ui/button";
import { useNavigate } from "react-router-dom";

/**
 * Banner shown when a user is at/over their plan's max_locations.
 * status shape (from GET /api/store/location-limit-status):
 *   { allowed, current, limit, unlimited, over_limit, in_grace, grace_ends_at, plan_name }
 */
const LocationLimitBanner = ({ status }) => {
  const navigate = useNavigate();
  if (!status || status.unlimited) return null;
  if (!status.over_limit && status.allowed) return null;

  // Compute days remaining in grace
  let daysLeft = null;
  if (status.over_limit && status.in_grace && status.grace_ends_at) {
    try {
      const ends = new Date(status.grace_ends_at);
      const ms = ends - new Date();
      daysLeft = Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
    } catch { /* ignore */ }
  }

  const overLimitGracePassed = status.over_limit && !status.in_grace;
  const isOverLimit = status.over_limit;
  const atLimit = !isOverLimit && !status.allowed;

  let tone = "warning"; // amber for at-limit or in-grace
  if (overLimitGracePassed) tone = "danger";

  const styles = {
    warning: "border-amber-500/30 bg-amber-500/5 text-amber-700 dark:text-amber-300",
    danger: "border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-300",
  };

  let title, body;
  if (overLimitGracePassed) {
    title = `Account over location limit — restrictions active`;
    body = `You have ${status.current} locations but your ${status.plan_name || "current"} plan allows only ${status.limit}. The ${status.grace_days}-day grace period has ended. Adding new locations is blocked. Upgrade or archive locations to come back into compliance.`;
  } else if (isOverLimit) {
    title = `You're over your plan's location limit`;
    body = `You have ${status.current} locations but your ${status.plan_name || "current"} plan allows only ${status.limit}. ${daysLeft !== null ? `You have ${daysLeft} day${daysLeft === 1 ? "" : "s"} left to upgrade or archive locations before restrictions kick in.` : "Please upgrade or archive locations."}`;
  } else if (atLimit) {
    title = `You've reached your plan's location limit`;
    body = `Your ${status.plan_name || "current"} plan allows up to ${status.limit} location${status.limit === 1 ? "" : "s"}. Upgrade to a multi-location plan to add more.`;
  }

  return (
    <div className={`border rounded-lg p-4 flex items-start gap-3 ${styles[tone]}`} data-testid="location-limit-banner">
      <div className="shrink-0 mt-0.5">
        {tone === "danger" ? <AlertTriangle className="w-5 h-5" /> : <Building2 className="w-5 h-5" />}
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-semibold text-sm mb-1">{title}</h4>
        <p className="text-sm opacity-90">{body}</p>
      </div>
      <Button
        size="sm"
        className={tone === "danger" ? "bg-red-600 text-white hover:bg-red-600/90 shrink-0" : "bg-emerald-600 text-white hover:bg-emerald-600/90 shrink-0"}
        onClick={() => navigate("/dashboard/settings?tab=my-plan")}
        data-testid="location-limit-upgrade-btn"
      >
        Upgrade <ArrowRight className="ml-1.5 w-3.5 h-3.5" />
      </Button>
    </div>
  );
};

export default LocationLimitBanner;
